/**
 * Button — primitive bouton avec variants et tailles.
 *
 * @remarks
 * 4 variants (primary/secondary/danger/ghost) × 3 tailles (sm/md/lg). API
 * inspirée du legacy mais réécrite en Tailwind + `cn`.
 *
 * `loading` reprend la grammaire d'`AuthButton` (Spinner + désactivation) :
 * le cercle de chargement local d'un bouton qui déclenche une mutation,
 * à la place du voile plein écran (`GlobalLoader`) — cf. espace admin.
 *
 * **Le seul bouton plein** (SAAS-1185). `primary` et `danger` posent l'accent
 * (ou l'erreur) assombri — `bg-primary-strong`, `bg-error-strong` — avec un
 * texte `on-primary` / `on-error` : lisible (≥ 4,5:1) dans les deux thèmes et
 * sur un accent personnalisé, ce que le blanc sur l'accent pur n'était pas
 * (4,35:1 en clair, 2,99:1 en sombre). Un `<button>` plein fait main est
 * compté par `lint:button-ratchet`.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from './spinner';
import { cn } from '@/lib/utils/cn';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  active?: boolean;
  /** Affiche le `Spinner` à la place du contenu et désactive le bouton. */
  loading?: boolean;
  /** Remplace le contenu pendant `loading` (sinon le contenu normal reste, à côté du Spinner). */
  loadingLabel?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary-strong text-on-primary hover:bg-primary-strong-hover',
  secondary: 'border border-border-strong bg-surface text-fg hover:bg-bg-muted',
  danger: 'bg-error-strong text-on-error hover:bg-error-strong-hover',
  ghost: 'text-fg-muted hover:bg-bg-muted hover:text-fg',
};

/**
 * Les icônes posées dans le bouton prennent la taille de son texte (SAAS-1184),
 * sauf si l'appelant a donné une classe `size-*` explicite sur l'icône.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-xs [&_svg:not([class*=size-])]:size-(--icon-size-xs)',
  md: 'h-9 px-3.5 text-sm [&_svg:not([class*=size-])]:size-(--icon-size-sm)',
  lg: 'h-11 px-5 text-base [&_svg:not([class*=size-])]:size-(--icon-size-md)',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    active = false,
    loading = false,
    loadingLabel,
    className,
    type = 'button',
    disabled,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={loading || (disabled ?? false)}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors',
        'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        active && 'ring-primary ring-2 ring-offset-1',
        className,
      )}
      {...rest}
    >
      {loading && <Spinner size="sm" />}
      {loading ? (loadingLabel ?? children) : children}
    </button>
  );
});
